import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { Formik } from 'formik';
import { observer } from 'mobx-react';
import { useLazyQuery } from '@apollo/client';
import _cloneDeep from 'lodash-es/cloneDeep';

import { PolicyTabs } from 'core/store/account-store/policies-store/policy-store/policy-store';
import { dateFormatter } from 'core/helpers/formatters';
import Label from 'core/components/label/label';
import Modal from 'core/components/modal/modal';
import { FormField } from 'core/components/form';
import { useStore } from 'core/store';
import { Spinner } from 'core/components/spinner';
import { NotificationCard } from 'core/components/notification-card';
import icInfoIcon from 'core/assets/svg/info-icon-dark.svg';
import { getInitialValues, optionsForPolicyType, getUpdatedPolicyValues } from './helpers';
import { GET_POLICY_FROM_VERSION } from './revert-version.queries';
import { useStyles } from '../versions.styles';

/**
 * RevertVersionModal component provides a modal interface for reverting a policy's segment details
 * to a specified version. It fetches the policy details for the version and allows the user
 * to select which characteristics to revert to.
 *
 * The changes will be applied to the current change (aka segment) that is selected on the policy.
 *
 * @param {boolean} props.open - Determines if the modal is open.
 * @param {function} props.onClose - Callback function to close the modal.
 * @param {number} props.versionToRevertTo - The policy version number to revert to.
 * @param {string} props.policyType - The type of policy being handled.
 * @returns {JSX.Element} The rendered component.
 */
const RevertVersionModal = ({ open, onClose, versionToRevertTo, policyType, closeVersionHistoryModal }) => {
  const classes = useStyles();
  const {
    account: {
      policies: { policy: policyStore }
    }
  } = useStore();

  const [updateError, setUpdateError] = useState(false);
  const [getPolicyFromVersion, { loading, error }] = useLazyQuery(GET_POLICY_FROM_VERSION, {
    variables: { id: policyStore.policy.id, version: String(versionToRevertTo) }
  });

  return (
    <Formik
      initialValues={getInitialValues({
        policyType,
        versionToRevertTo,
        segmentStartDate: policyStore.segment.startDate
      })}
      validate={(values) => {
        const errors = {};
        if (
          Object.keys(values)
            .filter((o) => o !== 'clarionDoorIdOverride') // don't validate clarionDoorIdOverride checkbox
            .every((key) => !values[key])
        ) {
          errors.message = 'Please select at least one characteristic';
        }
        return errors;
      }}
      validateOnMount={false}
      onSubmit={async (values) => {
        setUpdateError(false);
        const res = await getPolicyFromVersion();
        if (res.data?.getPolicyFromVersion) {
          const policyFromVersion = res.data.getPolicyFromVersion;
          const nodesToUpdate = Object.keys(values).filter((key) => Boolean(values[key]));
          const updatedPolicyValues = getUpdatedPolicyValues({
            policy: _cloneDeep(policyStore.policy),
            policyFromVersion,
            nodesToUpdate,
            selectedSegmentStartDate: policyStore.segment.startDate
          });

          closeVersionHistoryModal();
          onClose();
          policyStore.setCurrentTab(PolicyTabs.DETAILS);
          const changeSegmentRes = await policyStore.changeSegment(
            policyStore.policy.id,
            updatedPolicyValues,
            true,
            updatedPolicyValues.clarionDoorIdOverride
          );
          if (!changeSegmentRes.ok && changeSegmentRes.error) {
            setUpdateError(changeSegmentRes.error);
          }
        }
      }}
    >
      {({ handleSubmit, errors }) => (
        <Modal
          open={open}
          onClose={onClose}
          title={`Would you like to apply details from version ${versionToRevertTo} to the selected segment?`}
          primaryActionText={`Preview policy with details from version ${versionToRevertTo}`}
          onPrimaryAction={handleSubmit}
          loading={loading}
        >
          {/* CHOOSE CHARACTERISTICS TO REVERT */}
          {!loading && (
            <Grid container className={classes.revertModalBody}>
              <Grid item xs={12}>
                <Label type="infoSubtitle">
                  If so, which details from version {versionToRevertTo} would you like to apply?
                </Label>
              </Grid>
              <Grid item xs={12}>
                <Label type="infoSubtitle">
                  They will be applied to the policy history segment starting at{' '}
                  <b>&nbsp;{dateFormatter(policyStore.segment.startDate)}</b>.
                </Label>
              </Grid>

              <Grid container spacing={2} className={classes.options}>
                <Label type="darkGreenSmallBold">Characteristics</Label>
                {/* Farm products (F/Z/Y) have no revertable-characteristic mapping — body.jsx
                    already suppresses the Revert affordance for them, but default to [] so an
                    unmapped policyType can never crash the modal again (GH #33514). */}
                {(optionsForPolicyType[policyType] ?? []).map((option) => (
                  <Grid key={option.id} item xs={12}>
                    <FormField id={option.id} name={option.id} type="checkbox" label={option.value} mode="light" />
                  </Grid>
                ))}
              </Grid>

              <Grid container spacing={2} className={classes.options}>
                <Label type="darkGreenSmallBold">Options</Label>
                <Grid item xs={12}>
                  <FormField
                    id="clarionDoorIdOverride"
                    name="clarionDoorIdOverride"
                    type="checkbox"
                    label={`Use version ${versionToRevertTo} ClarionDoor rating`}
                    mode="light"
                  />
                </Grid>
              </Grid>

              <NotificationCard type="light" icon={icInfoIcon} iconSize="icon16">
                <Label type="body2">
                  Want to apply details to a different policy history segment? Select a new segment in the Changes
                  History located on the sidebar of the policy page.
                </Label>
              </NotificationCard>
            </Grid>
          )}

          {/* LOADING WHILE GRABBING POLICY FROM POLICYAUDIT TABLE */}
          {loading && (
            <Grid container className={classes.loadingContainer}>
              <Spinner size={102} />
              <Label type="darkSmall" className={classes.loadingText}>
                Grabbing details from the version {versionToRevertTo} of this policy. Please wait...
              </Label>
            </Grid>
          )}

          {/* ERRORS FROM VALIDATING FORM */}
          {Object.keys(errors).length > 0 && (
            <Grid container className={classes.loadingContainer}>
              <Label type="largeWarning">Error: {errors.message}</Label>
            </Grid>
          )}

          {/* ERRORS FROM FETCHING POLICY FROM POLICYAUDIT TABLE */}
          {error && (
            <Grid container className={classes.loadingContainer}>
              <Label type="darkSmall" className={classes.loadingText}>
                An error occurred while grabbing version {versionToRevertTo} of this policy. Please try again.
                {error?.message && <br />}
                {error?.message}
              </Label>
            </Grid>
          )}

          {/* ERRORS FROM UPDATING POLICY */}
          {updateError && (
            <Grid container className={classes.loadingContainer}>
              <Label type="darkSmall" className={classes.loadingText}>
                An error occurred while trying to update the policy. Please try again or contact support.
                {updateError?.message && <br />}
                {updateError?.message}
              </Label>
            </Grid>
          )}
        </Modal>
      )}
    </Formik>
  );
};

RevertVersionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  versionToRevertTo: PropTypes.number.isRequired,
  policyType: PropTypes.string.isRequired,
  closeVersionHistoryModal: PropTypes.func.isRequired
};

export default observer(RevertVersionModal);
